import { unref, isRef, shallowRef, ref, watch } from 'vue'
// getCatalogEntry (constants/catalogs), catalogVersion y slugFromCacheKey
// (composables/useCatalogCache) llegan por auto-import de Nuxt.

interface CachePayload<T> {
  timestamp: number
  data: T[]
}

interface PaginatedElements<T> {
  totalRecords: number
  pageSize: number
  currentPage: number
  totalPages: number
  records: T[]
}

const DEFAULT_TTL = 1000 * 60 * 60 // 1 hora

// Sentinel para filter(): usar en lugar de un valor exacto cuando se quiere
// exigir que el campo tenga algún valor (no null/undefined/'').
export const NOT_NULL = Symbol('NOT_NULL')

function matchesFilter(itemValue: unknown, filterValue: unknown): boolean {
  if (filterValue === NOT_NULL) {
    return itemValue !== null && itemValue !== undefined && itemValue !== ''
  }
  return String(itemValue) === String(filterValue)
}

type EndpointRef = Ref<string> | ComputedRef<string> | string

export interface CatalogoPlanoOptions {
  /** Sobrescribe el endpoint por defecto del registro (para URLs con parámetros). */
  endpoint?: EndpointRef
  ttl?: number
  saveOnCache?: boolean
  paged?: boolean
  /** Sobrescribe la cacheKey canónica del registro (casos muy puntuales). */
  cacheKey?: string
}

function isEndpointArg(arg: unknown): arg is EndpointRef {
  return typeof arg === 'string' || isRef(arg)
}

/**
 * Catálogo "plano" cacheado en localStorage.
 *
 * Dos formas de invocación:
 *
 *   // 1) por slug del registro (constants/catalogs.ts) — preferida
 *   useCatalogoPlano<Measurement>('measurements')
 *   useCatalogoPlano<Product>('products', { endpoint: urlConParams, paged: true, saveOnCache: false })
 *
 *   // 2) legacy posicional (cacheKey + endpoint sueltos) — sigue soportada
 *   useCatalogoPlano<Measurement>('catalogo-measurement', '/meassurement')
 *
 * En ambos casos, si la cacheKey resultante pertenece a un catálogo del
 * registro, la instancia se recarga automáticamente cuando se invalida ese
 * catálogo (ver invalidateCatalog()).
 */
export function useCatalogoPlano<T>(
  slugOrCacheKey: string,
  endpointOrOptions?: EndpointRef | CatalogoPlanoOptions,
  ttl: number = DEFAULT_TTL,
  saveOnCache: boolean = true,
  paged: boolean = false
) {
  let cacheKey: string
  let endpoint: EndpointRef

  if (isEndpointArg(endpointOrOptions)) {
    // --- forma legacy posicional ---
    cacheKey = slugOrCacheKey
    endpoint = endpointOrOptions
  } else {
    // --- forma por slug (con o sin opciones) ---
    const opts = (endpointOrOptions ?? {}) as CatalogoPlanoOptions
    const entry = getCatalogEntry(slugOrCacheKey)

    cacheKey = opts.cacheKey ?? entry?.cacheKeys[0] ?? slugOrCacheKey
    endpoint = opts.endpoint ?? entry?.endpoint ?? ''

    if (opts.ttl !== undefined) ttl = opts.ttl
    if (opts.saveOnCache !== undefined) saveOnCache = opts.saveOnCache
    if (opts.paged !== undefined) paged = opts.paged

    if (!entry && !opts.endpoint) {
      console.warn(
        `useCatalogoPlano('${slugOrCacheKey}'): slug no encontrado en CATALOG_REGISTRY y sin endpoint; ` +
        `el catálogo no cargará.`
      )
    }
  }

  // slug lógico para la suscripción reactiva a invalidateCatalog()
  const slug = getCatalogEntry(slugOrCacheKey)
    ? slugOrCacheKey
    : slugFromCacheKey(cacheKey)

  const rawItems = shallowRef<T[]>([])
  const items = shallowRef<T[]>([])
  const itemsPaginated = shallowRef<PaginatedElements<T>>()
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const activeFilters = ref<Partial<Record<keyof T, any>>>({})

  function applyActiveFilters() {
    const filters = activeFilters.value
    const keys = (Object.keys(filters) as (keyof T)[])
      .filter(key => filters[key] !== undefined && filters[key] !== null && filters[key] !== '')

    items.value = keys.length
      ? rawItems.value.filter(item =>
        keys.every(key => matchesFilter(item[key], filters[key]))
      )
      : rawItems.value
  }

  // Filtra localmente el catálogo ya cargado (sin volver a llamar al endpoint).
  // El filtro queda activo: llamadas posteriores a load()/filter() se reaplican sobre él.
  // No aplica en modo paginado: totalRecords/totalPages vienen del servidor y
  // quedarían desincronizados respecto a los items filtrados localmente.
  function filter(filters: Partial<Record<keyof T, any>>) {
    if (paged) {
      console.warn(`useCatalogoPlano('${cacheKey}'): filter() no está soportado en catálogos paginados`)
      return
    }
    activeFilters.value = filters
    applyActiveFilters()
  }

  function clearFilter() {
    if (paged) return
    activeFilters.value = {}
    items.value = rawItems.value
  }

  function loadFromCache(): T[] | null {
    const raw = localStorage.getItem(cacheKey)

    if (!raw) return null

    try {
      const parsed: CachePayload<T> = JSON.parse(raw)

      // Validar que realmente sea un array
      if (!Array.isArray(parsed.data)) {
        localStorage.removeItem(cacheKey)
        return null
      }

      // Cache expirado
      if (Date.now() - parsed.timestamp > ttl) {
        localStorage.removeItem(cacheKey)
        return null
      }

      return parsed.data as T[]

    } catch {
      localStorage.removeItem(cacheKey)
      return null
    }
  }

  function saveToCache(data: T[]) {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        timestamp: Date.now(),
        data: Array.isArray(data) ? data : []
      })
    )
  }

  async function load(
    force = false,
    options?: { append?: boolean }
  ) {
    if (!options?.append && rawItems.value.length && !force) return

    const cached = !force && !options?.append ? loadFromCache() : null
    if (cached) {
      rawItems.value = cached
      if (paged) {
        items.value = cached
      } else {
        applyActiveFilters()
      }
      return
    }

    loading.value = true
    error.value = null

    try {
      const url = unref(endpoint)

      if (!paged) {
        const { data } = await useApiFetchArray<T>(url)
        if (!data.value) throw new Error('Catálogo vacío')

        const records = Array.isArray(data.value.data)
          ? data.value.data as T[]
          : []

        if (options?.append) {
          rawItems.value = [
            ...rawItems.value,
            ...records
          ]

        } else {
          rawItems.value = records
        }
      }

      if (paged) {
        const dataPaged = await useApiFetchPage<T>(url)

        if (!dataPaged?.data)
          throw new Error('Catálogo vacío')

        const res = dataPaged.data

        const records = Array.isArray(res.records)
          ? res.records as T[]
          : []


        if (options?.append && itemsPaginated.value) {
          const prev = itemsPaginated.value.records ?? []

          itemsPaginated.value = {
            ...res,
            records: [
              ...prev,
              ...records
            ]
          }

          rawItems.value = itemsPaginated.value.records
        } else {
          itemsPaginated.value = {
            ...res,
            records
          }
          rawItems.value = records
        }
      }

      if (!paged) applyActiveFilters()

      if (saveOnCache && !options?.append) {
        saveToCache(rawItems.value)
      }

    } catch (e: any) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  // Recarga automática cuando se invalida este catálogo (crear/editar/borrar/
  // importar en cualquier parte, o en otra pestaña vía el plugin cross-tab).
  if (slug) {
    watch(catalogVersion(slug), (nv, ov) => {
      if (nv !== ov) load(true)
    })
  }

  return {
    items,
    itemsPaginated,
    loading,
    error,
    load,
    filter,
    clearFilter
  }
}
